import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Send } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useSendQuestionnaire, useFormsTenantStatus } from '@/hooks/useClinioForms';
import { useFormsTemplateList } from '@/hooks/useFormsTemplates';

interface SendFormsAssignmentModalProps {
  patientId: string;
  clinicId: string;
  onClose: () => void;
}

export function SendFormsAssignmentModal({
  patientId,
  clinicId,
  onClose,
}: SendFormsAssignmentModalProps) {
  const { t } = useTranslation();
  const { data: tenantStatus } = useFormsTenantStatus(clinicId);
  const { data: templateLibrary, isLoading: templatesLoading } = useFormsTemplateList(clinicId);
  const templates = [...(templateLibrary?.platform ?? []), ...(templateLibrary?.clinic ?? [])];
  const sendMutation = useSendQuestionnaire();

  const defaultLocale = (tenantStatus?.default_locale ?? 'da-DK') as 'da-DK' | 'en-DK';

  const [templateId, setTemplateId] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [locale, setLocale] = useState<'da-DK' | 'en-DK'>(defaultLocale);
  const [error, setError] = useState<string | null>(null);

  const tenantNotActive = !tenantStatus || tenantStatus.status !== 'active';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!templateId) {
      setError(t('forms.modal.error_template_required', 'Vælg venligst et spørgeskema'));
      return;
    }

    try {
      await sendMutation.mutateAsync({
        patient_id:  patientId,
        template_id: templateId,
        due_at:      dueAt ? new Date(dueAt).toISOString() : null,
        locale,
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('forms.modal.error_send', 'Kunne ikke sende spørgeskema. Prøv igen om et øjeblik.'),
      );
    }
  };

  return (
    <Modal title={t('forms.modal.title', 'Send spørgeskema')} onClose={onClose}>
      {tenantNotActive ? (
        <div className="rounded-lg bg-yellow-50 p-4 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
          {t(
            'forms.modal.tenant_not_active',
            'Klinikken er ikke koblet til Clinio Forms endnu. Kontakt administratoren for at aktivere integrationen.',
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Template picker */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('forms.modal.template_label', 'Spørgeskema')}
            </label>
            {templatesLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('forms.modal.loading_templates', 'Henter skabeloner…')}
              </div>
            ) : (
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                required
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              >
                <option value="">{t('forms.modal.select_template', '— Vælg spørgeskema —')}</option>
                {(templates ?? []).map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {locale === 'en-DK' && tpl.display_name_en
                      ? tpl.display_name_en
                      : tpl.display_name_da}
                    {tpl.seed_status === 'content_pending_license' ? ' ⚠' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Optional due date */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('forms.modal.due_date_label', 'Frist (valgfri)')}
            </label>
            <input
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Locale */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('forms.modal.locale_label', 'Sprog')}
            </label>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value as 'da-DK' | 'en-DK')}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              <option value="da-DK">{t('forms.locale.da_DK', 'Dansk (da-DK)')}</option>
              <option value="en-DK">{t('forms.locale.en_DK', 'Engelsk (en-DK)')}</option>
            </select>
          </div>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={sendMutation.isPending}>
              {t('common.cancel', 'Annuller')}
            </Button>
            <Button type="submit" disabled={sendMutation.isPending || !templateId}>
              {sendMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {t('forms.modal.send_button', 'Send spørgeskema')}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
